import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Search,
  Plus,
  Download,
  Eye,
  PauseCircle,
  KeyRound,
  CreditCard,
  Globe,
  X,
  LayoutGrid,
  List,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  TrendingUp,
  Mail,
} from "lucide-react";

import BaseModal from "../../../components/BaseModal/BaseModal";
import StatusBadge, {
  StatusVariant,
} from "../../../components/StatusBadge/StatusBadge";
import Button from "../../../components/Button/Button";
import DataTable, { Column } from "../../../components/DataTable/DataTable";
import Pagination from "../../../components/Pagination/Pagination";

import styles from "./Tenants.module.css";
import axios, { AxiosError } from "axios";
import { log } from "node:console";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import * as XLSX from "xlsx";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API_GET = `${BASE_URL}/tenants/get`;
const API_POST = `${BASE_URL}/tenants/add`;
const API_GET_PLAN = `${BASE_URL}/fly/get`;
const API_DELETE = `${BASE_URL}/tenants/delete`;
const API_SUSPEND = `${BASE_URL}/tenants/update`;
const ALI_ALL_SUSPEND = `${BASE_URL}/tenants/all-put`;

interface Tenant {
  id: string;
  name: string;
  email: string;
  domain: string;
  plan: "Starter" | "Pro" | "Enterprise";
  status: "Active" | "Suspended" | "Trial" | "Expired";
  modules: string[];
  agents: number;
  created: string;
  mrr: number;
  country: string;
  flag: string;
}

interface Plan {
  id: number;
  name: string;
  monthlyprice: number | string;
  yeralyprice: number | string;
  status: string;
  modules: string[];
  subscribers: number;
  mrr: number;
}

interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
}

interface ImportMetaEnv {
  readonly VITE_API_BASE_URL: string;
  readonly VITE_ENV: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

function planVariant(p: Tenant["plan"]): StatusVariant {
  return p.toLowerCase() as StatusVariant;
}
function statusVariant(s: Tenant["status"]): StatusVariant {
  return s.toLowerCase() as StatusVariant;
}

const TenantAvatar: React.FC<{ name: string; size?: number }> = ({
  name,
  size = 32,
}) => (
  <div
    className={styles.avatar}
    style={{ width: size, height: size, fontSize: size * 0.34 }}
  >
    {name.slice(0, 2).toUpperCase()}
  </div>
);

const ModuleTags: React.FC<{ modules: string[]; max?: number }> = ({
  modules,
  max = 3,
}) => (
  <div className={styles.moduleTags}>
    {modules.slice(0, max).map((m) => (
      <span key={m} className={styles.moduleTag}>
        {m}
      </span>
    ))}
    {modules.length > max && (
      <span className={styles.moduleTagMore}>+{modules.length - max}</span>
    )}
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub: string;
  icon: React.ReactNode;
  accent?: boolean;
}> = ({ label, value, sub, icon, accent }) => (
  <div className={`${styles.statCard} ${accent ? styles.statCardAccent : ""}`}>
    <div className={styles.statTop}>
      <span className={styles.statLabel}>{label}</span>
      <span
        className={`${styles.statIcon} ${accent ? styles.statIconAccent : ""}`}
      >
        {icon}
      </span>
    </div>
    <div
      className={`${styles.statValue} ${accent ? styles.statValueAccent : ""}`}
    >
      {value}
    </div>
    <div className={styles.statSub}>{sub}</div>
  </div>
);

const TenantDetailDrawer: React.FC<{
  tenant: Tenant | null;
  onClose: () => void;
  onSuspendClick: (tenant: Tenant) => void;
}> = ({ tenant, onClose, onSuspendClick }) => {
  if (!tenant) return null;
  return (
    <>
      <div className={styles.drawerBackdrop} onClick={onClose} />
      <aside className={styles.drawer} aria-label="Tenant details">
        <div className={styles.drawerHeader}>
          <div className={styles.drawerTitleRow}>
            <TenantAvatar name={tenant.name} size={44} />
            <div>
              <h3 className={styles.drawerName}>{tenant.name}</h3>
              <span className={styles.drawerId}>{tenant.id}</span>
              <div className={styles.drawerBadges}>
                <StatusBadge variant={statusVariant(tenant.status)} size="sm" />
                <StatusBadge variant={planVariant(tenant.plan)} size="sm" />
              </div>
            </div>
          </div>
          <button
            className={styles.drawerClose}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>

        <div className={styles.drawerBody}>
          <div className={styles.drawerStats}>
            {[
              { l: "MRR", v: tenant.mrr > 0 ? `$${tenant.mrr}` : "—" },
              { l: "Agents", v: String(tenant.agents) },
              { l: "Since", v: tenant.created.split(",")[0] },
            ].map((s) => (
              <div key={s.l} className={styles.drawerStatItem}>
                <span className={styles.drawerStatLabel}>{s.l}</span>
                <span className={styles.drawerStatValue}>{s.v}</span>
              </div>
            ))}
          </div>

          <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>Account Details</h4>
            {[
              { label: "Admin Email", value: tenant.email },
              { label: "Domain", value: tenant.domain },
              { label: "Country", value: `${tenant.flag} ${tenant.country}` },
              { label: "Registered", value: tenant.created },
            ].map((row) => (
              <div key={row.label} className={styles.drawerRow}>
                <span className={styles.drawerRowLabel}>{row.label}</span>
                <span className={styles.drawerRowValue}>{row.value}</span>
              </div>
            ))}
          </section>

          <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>Enabled Modules</h4>
            <div className={styles.drawerModules}>
              {tenant.modules.map((m) => (
                <span key={m} className={styles.drawerModule}>
                  {m}
                </span>
              ))}
            </div>
          </section>

          <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>Admin Actions</h4>
            <div className={styles.drawerActions}>
              <Button variant="ghost" size="sm" icon={<Eye size={14} />}>
                View Full Profile
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<KeyRound size={14} />}
              >
                Reset Password
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<CreditCard size={14} />}
              >
                View Subscription
              </Button>
              <Button variant="secondary" size="sm" icon={<Globe size={14} />}>
                View Domain
              </Button>
              {tenant.status !== "Suspended" ? (
                <Button
                  variant="danger"
                  size="sm"
                  icon={<PauseCircle size={14} />}
                  onClick={() => onSuspendClick(tenant)}
                >
                  Suspend Tenant
                </Button>
              ) : (
                <Button
                  variant="warning"
                  size="sm"
                  icon={<CheckCircle size={14} />}
                  onClick={() => onSuspendClick(tenant)}
                >
                  Reactivate Tenant
                </Button>
              )}
            </div>
          </section>
        </div>
      </aside>
    </>
  );
};

const SuspendModal: React.FC<{
  tenant: Tenant | null;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ tenant, onClose, onConfirm }) => (
  <BaseModal
    isOpen={!!tenant}
    onClose={onClose}
    title={tenant?.status === "Suspended" ? "Reactivate" : "Suspend"}
    subtitle={tenant?.name}
    size="sm"
    footer={
      <>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={onConfirm}>
          {tenant?.status === "Suspended" ? "Reactivate" : "Suspend"}
        </Button>
      </>
    }
  >
    {tenant && (
      <div className={styles.confirmBody}>
        <div className={styles.confirmIcon}>
          <AlertTriangle size={24} />
        </div>
        <p className={styles.confirmText}>
          Are you sure you want to{" "}
          {tenant.status === "Suspended" ? "reactivate" : "suspend"}{" "}
          <strong>{tenant.name}</strong>? This will immediately disable their
          platform access.
        </p>
        <div className={styles.confirmMeta}>
          <span>{tenant.id}</span>
          <span>{tenant.email}</span>
        </div>
      </div>
    )}
  </BaseModal>
);

// add tenants form
interface addTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  plans: Plan[];
}

const AddTenantModal: React.FC<addTenantModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  plans,
}) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    domain: "",
    planId: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set =
    (k: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    // if (!form.name || !form.email || !form.domain || !form.planId) return;

    try {
      setSubmitting(true);

      const payload = {
        agency_name: form.name,
        admin_email: form.email,
        subdomain: form.domain,
        plan_id: Number(form.planId),
      };

      await axios.post(API_POST, payload);
      onCreated();
      onClose();
      setForm({
        name: "",
        email: "",
        domain: "",
        planId: "",
      });

      toast.success("created..");
    } catch (err) {
      const error = err as AxiosError<ApiError>;

      const message =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong";

      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Tenant"
      subtitle="Register a new agency on the FlyGDS platform"
      size="md"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={<Plus size={14} />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Creating...." : "Create tenant"}
          </Button>
        </>
      }
    >
      <div className={styles.formGrid}>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Agency Name <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            placeholder="e.g. Al-Safar Travel"
            value={form.name}
            onChange={set("name")}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Admin Email <span className={styles.req}>*</span>
          </label>
          <input
            className={styles.input}
            type="email"
            placeholder="admin@agency.com"
            value={form.email}
            onChange={set("email")}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Subdomain <span className={styles.req}>*</span>
          </label>
          <div className={styles.inputAddon}>
            <input
              className={`${styles.input} ${styles.inputWithAddon}`}
              placeholder="agency-name"
              value={form.domain}
              onChange={set("domain")}
            />
            <span className={styles.addonText}>.flygds.app</span>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Subscription Plan <span className={styles.req}>*</span>
          </label>
          <select
            className={styles.select}
            value={form.planId}
            onChange={set("planId")}
          >
            <option value="">Select plan…</option>
            {plans
              .filter((p) => p.status?.toLowerCase() === "active")
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — ₹{p.monthlyprice}/mo
                </option>
              ))}
          </select>
        </div>
      </div>
      <p className={styles.formNote}>
        A welcome email with login credentials will be sent to the admin email
        address. Domain provisioning will begin automatically.
      </p>
    </BaseModal>
  );
};

const TenantsPage: React.FC = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [planFilter, setPlanFilter] = useState("All");
  const [sortBy, setSortBy] = useState("name");
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [detailTenant, setDetailTenant] = useState<Tenant | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<Tenant | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const PER_PAGE = 8;

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // GET tenants
  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(API_GET);
      const apiTenants = res.data.data || [];

      const mapped: Tenant[] = apiTenants.map((t: any) => ({
        id: t.tenant_id || String(t.id),
        name: t.agency_name,
        email: t.admin_email,
        domain: t.subdomain,
        plan: t.plan_name || "Starter",
        status:
          t.status?.trim().charAt().toUpperCase() +
            t.status?.trim().slice(1).toLowerCase() || "Active",
        modules: t.modules || [],
        agents: t.agents || t.subscribers || 0,
        created: t.created_at
          ? new Date(t.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
        mrr: Number(t.mrr || 0),
        country: t.country || "IN",
        flag: t.flag || "🇮🇳",
      }));

      setTenants(mapped);
      // console.log(setTenants);
    } catch (err) {
      const error = err as AxiosError<ApiError>;

      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load tenants";

      toast.error(message);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // GET plans
  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(API_GET_PLAN);
      setPlans(res.data.data || []);
    } catch (err) {
      const error = err as AxiosError<ApiError>;

      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load plans";

      toast.error(message);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
    fetchPlans();
  }, [fetchTenants, fetchPlans]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tenants
      .filter(
        (t) =>
          (!q ||
            t.name.toLowerCase().includes(q) ||
            t.email.toLowerCase().includes(q) ||
            t.domain.toLowerCase().includes(q) ||
            t.id.toLowerCase().includes(q)) &&
          (statusFilter === "All" || t.status === statusFilter) &&
          (planFilter === "All" || t.plan === planFilter),
      )
      .sort((a, b) => {
        if (sortBy === "mrr") return b.mrr - a.mrr;
        if (sortBy === "agents") return b.agents - a.agents;
        if (sortBy === "newest")
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        return a.name.localeCompare(b.name);
      });
  }, [search, statusFilter, planFilter, sortBy, tenants]);

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const stats = useMemo(
    () => ({
      total: tenants.length,
      active: tenants.filter((t) => t.status?.toLowerCase() === "active")
        .length,
      trial: tenants.filter((t) => t.status?.toLowerCase() === "trial").length,
      suspended: tenants.filter((t) => t.status?.toLowerCase() === "suspended")
        .length,
      mrr: tenants.filter((t) => t.mrr > 0).reduce((s, t) => s + t.mrr, 0),
    }),
    [tenants],
  );

  const toggleRow = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedKeys((prev) =>
      prev.size === paged.length ? new Set() : new Set(paged.map((t) => t.id)),
    );
  }, [paged]);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setPlanFilter("All");
    setPage(1);
  };
  const hasFilters = search || statusFilter !== "All" || planFilter !== "All";

  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);

  const handleDeleteTenant = async () => {
    if (!deleteTarget) return;
    console.log("deleting ->", deleteTarget.id);
    try {
      await axios.delete(`${API_DELETE}/${deleteTarget.id}`);
      toast.success("deleted");
      setDeleteTarget(null);
      fetchTenants();
    } catch (err) {
      const error = err as AxiosError<ApiError>;
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete tenant";
      toast.error(message);
    }
  };

  const handleSuspendTenant = async () => {
    if (!suspendTarget) return;
    try {
      await axios.put(`${API_SUSPEND}/${suspendTarget.id}`);
      toast.success(
        suspendTarget.status === "Suspended"
          ? "Tenant reactivated"
          : "Tenant suspended",
      );

      setSuspendTarget(null);
      fetchTenants();
    } catch (err) {
      const error = err as AxiosError<ApiError>;
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to update status";
      toast.error(message);
    }
  };

  const columns: Column<Tenant>[] = [
    {
      key: "tenant",
      header: "TENANT",
      render: (t) => (
        <div className={styles.tenantCell}>
          <TenantAvatar name={t.name} />
          <div>
            <div className={styles.tenantName}>{t.name}</div>
            <div className={styles.tenantMeta}>
              {t.flag} {t.email}
            </div>
          </div>
        </div>
      ),
    },
    // {
    //   key: "domain",
    //   header: "DOMAIN",
    //   render: (t) => <span className={styles.domainChip}>{t.domain}</span>,
    // },
    {
      key: "plan",
      header: "PLAN",
      render: (t) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>{t.plan}</span>
        </div>
      ),
    },
    {
      key: "status",
      header: "STATUS",
      render: (t) => (
        <StatusBadge variant={statusVariant(t.status)} size="sm" />
      ),
    },
    // {
    //   key: "agents",
    //   header: "AGENTS",
    //   render: (t) => <span className={styles.numericCell}>{t.agents}</span>,
    // },
    {
      key: "mrr",
      header: "MRR",
      render: (t) => (
        <span
          className={`${styles.numericCell} ${t.mrr === 0 ? styles.numericMuted : ""}`}
        >
          {t.mrr > 0 ? `$${t.mrr}` : "—"}
        </span>
      ),
    },
    {
      key: "modules",
      header: "MODULES",
      render: (t) => <ModuleTags modules={t.modules} />,
    },
    {
      key: "created",
      header: "JOINED",
      render: (t) => <span className={styles.dateText}>{t.created}</span>,
    },
    {
  key: "Invoice",
  header: "Invoice",
  render: (t) => (
    <span className={styles.dateText}>
       <Mail  size={15} onClick={() => navigate(`/dashboard/tenant-invoice/${t.id}`)} style={{cursor:"pointer" , marginLeft:"15px"}} />
    </span>
  ),
},
    {
      key: "actions",
      header: "Actions",
      render: (t) => (
        <div
          className={`${styles.actionBtns} actionBtns`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={styles.actionBtn}
            title="View details"
            onClick={() => setDetailTenant(t)}
          >
            <Eye size={13} />
          </button>
          <button className={styles.actionBtn} title="Reset password">
            <KeyRound size={13} />
          </button>
          <button
            className={`${styles.actionBtn} ${styles.actionDanger}`}
            title={t.status === "Suspended" ? "Reactivate" : "Suspend"}
            onClick={() => setSuspendTarget(t)}
          >
            <PauseCircle size={13} />
          </button>
          <button
            className={`${styles.actionBtn} ${styles.actionDanger}`}
            title="Delete"
            onClick={() => setDeleteTarget(t)}
          >
            <X size={13} />
          </button>
        </div>
      ),
    },
  ];

const handleAllSuspend = async () => {
  if (selectedKeys.size === 0) return;

  try {
    const ids = Array.from(selectedKeys); 

    await axios.put(ALI_ALL_SUSPEND, { ids });

    toast.success("Selected tenants suspended");
    setSelectedKeys(new Set());
    fetchTenants();
  } catch (err) {
    const error = err as AxiosError<ApiError>;
    const message =
      error.response?.data?.message ||
      error.message ||
      "Failed to update selected tenants";
    toast.error(message);
  }
};

const handleExportExcel = () => {
   
    const rowsSource =
    selectedKeys.size > 0
      ? filtered.filter((t) => selectedKeys.has(t.id))
      : filtered;

  const dataToExport = rowsSource.map((t, index) => ({
    "Sl" : index + 1,
    ID: t.id,
    Name: t.name,
    Email: t.email,
    Domain: t.domain,
    Plan: t.plan,
    Modules:t.modules.join(", "),
    Status: t.status,
    MRR: t.mrr,
    Created: t.created,
}))

  const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Tenants");
  XLSX.writeFile(workbook, "tenants.xlsx");
}

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Tenants</h1>
          <p className={styles.pageSubtitle}>
            {stats.total} agencies registered on FlyGDS
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" size="md" icon={<Download size={14}/>} onClick={handleExportExcel}>
            Export Excel
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={<Plus size={14} />}
            onClick={() => setAddOpen(true)}
          >
            Add Tenant
          </Button>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <StatCard
          label="Total Tenants"
          value={stats.total}
          sub="+3 this week"
          icon={<Users size={15} />}
        />
        <StatCard
          label="Active"
          value={stats.active}
          sub={`${Math.round((stats.active / stats.total) * 100)}% of total`}
          icon={<CheckCircle size={15} />}
        />
        <StatCard
          label="On Trial"
          value={stats.trial}
          sub="Converts soon"
          icon={<Clock size={15} />}
        />
        <StatCard
          label="Suspended"
          value={stats.suspended}
          sub="Needs review"
          icon={<AlertTriangle size={15} />}
        />
        <StatCard
          label="Total MRR"
          value={`$${stats.mrr.toLocaleString()}`}
          sub="+18% MoM"
          icon={<TrendingUp size={15} />}
          accent
        />
      </div>

      <div className={styles.tableCard}>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <Search size={14} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search tenants…"
            />
            {search && (
              <button
                className={styles.searchClear}
                onClick={() => setSearch("")}
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className={styles.filterPills}>
            {(["All", "Active", "Trial", "Suspended", "Expired"] as const).map(
              (s) => (
                <button
                  key={s}
                  className={`${styles.pill} ${statusFilter === s ? styles.pillActive : ""}`}
                  onClick={() => {
                    setStatusFilter(s);
                    setPage(1);
                  }}
                >
                  {s}
                </button>
              ),
            )}
          </div>

          <div className={styles.selects}>
            <select
              className={styles.select}
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="All">All Plans</option>
              <option value="Starter">Starter</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
            </select>
            <select
              className={styles.select}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort: Name</option>
              <option value="mrr">Sort: MRR</option>
              <option value="agents">Sort: Agents</option>
              <option value="newest">Sort: Newest</option>
            </select>
          </div>

          {/* <div className={styles.viewToggle}>
            <button className={`${styles.viewBtn} ${viewMode === 'table' ? styles.viewBtnActive : ''}`} onClick={() => setViewMode('table')} title="Table"><List size={14} /></button>
            <button className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`} onClick={() => setViewMode('grid')} title="Grid"><LayoutGrid size={14} /></button>
          </div> */}
        </div>

        <div className={styles.resultsBar}>
          <span className={styles.resultsText}>
            Showing <strong>{paged.length}</strong> of{" "}
            <strong>{filtered.length}</strong> tenants
          </span>
          {hasFilters && (
            <button className={styles.clearFilters} onClick={clearFilters}>
              <X size={10} /> Clear filters
            </button>
          )}
          {selectedKeys.size > 0 && (
            <div className={styles.bulkBar}>
              <span>
                <strong>{selectedKeys.size}</strong> selected
              </span>
              <button
                className={styles.bulkAction}
                style={{ color: "#DC2626" }}
                onClick={handleAllSuspend}
              >
                Suspend
              </button>
              <button
                className={styles.bulkAction}
                onClick={() => setSelectedKeys(new Set())}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {viewMode === "table" && (
          <DataTable
            columns={columns}
            data={paged}
            keyExtractor={(t) => t.id}
            selectable
            selectedKeys={selectedKeys}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            // onRowClick={setDetailTenant}
            emptyState={
              <div className={styles.emptyState}>
                <p className={styles.emptyTitle}>No tenants found</p>
                <p className={styles.emptySub}>
                  Try adjusting your search or filters
                </p>
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear all filters
                  </Button>
                )}
              </div>
            }
          />
        )}

        {viewMode === "grid" && (
          <div className={styles.grid}>
            {paged.length === 0 && (
              <div className={styles.emptyState} style={{ gridColumn: "1/-1" }}>
                <p className={styles.emptyTitle}>No tenants found</p>
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear all filters
                  </Button>
                )}
              </div>
            )}
            {paged.map((t) => (
              <div
                key={t.id}
                className={styles.gridCard}
                onClick={() => setDetailTenant(t)}
              >
                <div className={styles.gridCardBody}>
                  <div className={styles.gridCardTop}>
                    <div className={styles.gridCardInfo}>
                      <TenantAvatar name={t.name} size={34} />
                      <div>
                        <div className={styles.tenantName}>{t.name}</div>
                        <div className={styles.tenantMeta}>
                          {t.flag} {t.country} · {t.id}
                        </div>
                      </div>
                    </div>
                    <StatusBadge variant={statusVariant(t.status)} size="sm" />
                  </div>

                  <span
                    className={styles.domainChip}
                    style={{ display: "inline-flex", marginBottom: 8 }}
                  >
                    {t.domain}
                  </span>

                  <div className={styles.gridCardMeta}>
                    <StatusBadge
                      variant={planVariant(t.plan)}
                      size="sm"
                      dot={false}
                    />
                    <span
                      className={styles.numericCell}
                      style={{ fontSize: 14 }}
                    >
                      {t.mrr > 0 ? (
                        `$${t.mrr}/mo`
                      ) : (
                        <span className={styles.numericMuted}>No billing</span>
                      )}
                    </span>
                  </div>

                  <ModuleTags modules={t.modules} max={4} />

                  <div className={styles.gridCardFooter}>
                    <span className={styles.dateText}>
                      {t.agents} agents · {t.created}
                    </span>
                    <div
                      className={styles.gridCardActions}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className={styles.actionBtn}
                        onClick={() => setDetailTenant(t)}
                      >
                        <Eye size={13} />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.actionDanger}`}
                        onClick={() => setSuspendTarget(t)}
                      >
                        <PauseCircle size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          perPage={PER_PAGE}
          onPageChange={(p) => {
            setPage(p);
            setSelectedKeys(new Set());
          }}
        />
      </div>

      <TenantDetailDrawer
        tenant={detailTenant}
        onClose={() => setDetailTenant(null)}
        onSuspendClick={(tenant) => {
          setSuspendTarget(tenant);
          setDetailTenant(null);
        }}
      />
      <SuspendModal
        tenant={suspendTarget}
        onClose={() => setSuspendTarget(null)}
        onConfirm={handleSuspendTenant}
      />
      <AddTenantModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={fetchTenants}
        plans={plans}
      />

      {/* Delete Confirmation Modal */}
      <BaseModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Module"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteTenant}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
        </p>
      </BaseModal>
    </div>
  );
};

export default TenantsPage;
